// Section 9: buildings in the decay phase. Condition falls 1 per season (not in the season a
// building is finished or repaired). Upkeep of 10% of the materials is due every 8 seasons and is
// paid automatically from stock when it can be (booked as an `other` flow): paying restores the
// wear since upkeep was last paid (B-04). Unpaid upkeep stays due (warning `upkeep-overdue`) and
// the building keeps wearing, so its effect drops with condition (D-13). Wells can freeze in a
// Harsh winter: at the end of Autumn each working well freezes with the land's chance (D-04) and
// every frozen well thaws at the end of Winter. Constants: config.building, config.land.
import { RESOURCES } from '../../core/constants';
import { invalidateMemo } from '../../core/memo';
import { BUILDING_MEMO } from '../../core/queries';
import { sortedIds } from '../../core/sorted';
import type { Building, BuildingTypeId, PhaseContext } from '../../core/types';
import { missingMaterial, upkeepCost } from '../shared/capacity';
import { addStock } from '../shared/stocks';

/** Section 9 names the well as the building that can freeze ("can freeze in a Harsh winter"). */
const FREEZES: readonly BuildingTypeId[] = ['well'];

/** The turn upkeep was last paid (or the building finished or repaired). */
function lastUpkeepTurn(ctx: PhaseContext, b: Building): number {
  return b.upkeepDueTurn - ctx.cfg.building.upkeepEverySeasons;
}

function wear(ctx: PhaseContext): void {
  const decay = ctx.cfg.building.decayPerSeason;
  for (const id of sortedIds(ctx.draft.buildings)) {
    const b = ctx.draft.buildings[id];
    if (b === undefined || lastUpkeepTurn(ctx, b) === ctx.season.turn) continue;
    if (decay > 0 && b.condition > 0) b.condition = Math.max(0, b.condition - decay);
  }
}

function payUpkeep(ctx: PhaseContext): void {
  const turn = ctx.season.turn;
  const cfg = ctx.cfg.building;
  let overdue = 0;
  for (const id of sortedIds(ctx.draft.buildings)) {
    const b = ctx.draft.buildings[id];
    if (b === undefined || turn < b.upkeepDueTurn) continue;
    const cost = upkeepCost(ctx.registry, ctx.cfg, b.type);
    if (missingMaterial(ctx.draft, cost) !== null) {
      overdue++;
      continue;
    }
    for (const r of RESOURCES) {
      const n = cost[r] ?? 0;
      if (n > 0) addStock(ctx, r, -n, 'other');
    }
    const restored = Math.min(
      100 - b.condition,
      cfg.decayPerSeason * (turn - lastUpkeepTurn(ctx, b)),
    );
    b.condition += Math.max(0, restored);
    b.upkeepDueTurn = turn + cfg.upkeepEverySeasons;
    ctx.report.add({
      kind: 'building-upkeep',
      buildingId: b.id,
      buildingType: b.type,
      materials: cost,
      restored: Math.max(0, restored),
    });
  }
  if (overdue > 0) ctx.report.add({ kind: 'warning', code: 'upkeep-overdue', amount: overdue });
}

function freezeAndThaw(ctx: PhaseContext): void {
  const season = ctx.season.season;
  if (season !== 'autumn' && season !== 'winter') return;
  const chance = ctx.cfg.land.wellFreezeChance[ctx.state.options.land];
  for (const id of sortedIds(ctx.draft.buildings)) {
    const b = ctx.draft.buildings[id];
    if (b === undefined || !FREEZES.includes(b.type)) continue;
    // End of Winter: the thaw.
    if (season === 'winter' && b.frozen) {
      b.frozen = false;
      ctx.report.add({
        kind: 'building-frozen',
        buildingId: b.id,
        buildingType: b.type,
        frozen: false,
      });
    }
    // End of Autumn: a Harsh winter may freeze the well for the season ahead (D-04).
    if (
      season === 'autumn' &&
      !b.frozen &&
      chance > 0 &&
      ctx.rng.happened(ctx.rng.chance(chance))
    ) {
      b.frozen = true;
      ctx.report.add({
        kind: 'building-frozen',
        buildingId: b.id,
        buildingType: b.type,
        frozen: true,
      });
    }
  }
}

/** Section 9 building upkeep for the decay phase: wear, upkeep, frost. */
export function maintainBuildings(ctx: PhaseContext): void {
  wear(ctx);
  payUpkeep(ctx);
  freezeAndThaw(ctx);
  invalidateMemo(ctx.draft, BUILDING_MEMO);
}
